import React, { useCallback, useContext, useState } from 'react';
import { Button, DatePicker, Flex, message, Modal, Switch } from 'antd';
import dayjs from 'dayjs';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import PackageContext from '@totem/components/software/software_package/packageContext';
import { VersionEndOfSupportUpdateRequest } from '@totem/components/software/software_package/types';
import { SoftwareVersion } from '@totem/components/software/software_report/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import FormFieldLabelLeft from '@totem/components/ui_components/forms/FormFieldLabelLeft';
import HeaderItem from '@totem/components/ui_components/headers/HeaderItem';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { isNotNull } from '@totem/utilities/common';
import { ParseDate } from '@totem/utilities/dateUtilities';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';
const calcIsEndOfSupport = (data: SoftwareVersion) => {
  if (isNotNull(data) && isNotNull(data.endOfSupport)) {
    if (data.endOfSupport.isEndOfSupport) {
      return true;
    }
    const eosDate = dayjs(data.endOfSupport.endOfSupportDate);
    if (eosDate.year() > 1970) {
      if (eosDate.valueOf() < dayjs().valueOf()) {
        return true;
      }
    }
  }
  return false;
};
const getndOfSupportDateDisplay = (data: SoftwareVersion) => {
  if (isNotNull(data) && isNotNull(data.endOfSupport)) {
    const eosDate = dayjs(data.endOfSupport.endOfSupportDate);
    if (eosDate.year() > 1970) {
      return eosDate.format('YYYY-MM-DD');
    }
  }
  return '';
};
const VersionEndOfSupport = () => {
  const { data, onAction, selectedVersion } = useContext(PackageContext);
  const versionData =
    isNotNull(data) && isNotNull(selectedVersion) && selectedVersion !== ''
      ? data.package.versions.find((chk) => chk.number === selectedVersion)
      : null;
  const isEndOfSupport = calcIsEndOfSupport(versionData);
  const endOfSupportDisplayDate = getndOfSupportDateDisplay(versionData);
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isEndOfSupportUI, setIsEndOfSupportUI] =
    useState<boolean>(isEndOfSupport);
  const [endOfSupportDateUI, setEndOfSupportDateUI] = useState<number>(
    endOfSupportDisplayDate === ''
      ? null
      : dayjs(endOfSupportDisplayDate, 'YYYY-MM-DD').valueOf(),
  );
  const sendUpdate = useCallback(
    async (request: VersionEndOfSupportUpdateRequest) => {
      if (isSending) {
        return;
      }
      fetch(`${DEVICES_ENDPOINT}/software/version/endOfSupport`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
      })
        .then((response) => {
          if (response.status !== 200) {
            message.error('End of support update failed!');
          } else {
            onAction('refresh');

            setShowEdit(false);
          }
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );
  const handleSubmit = () => {
    const request: VersionEndOfSupportUpdateRequest = {
      packageId: data.package.id,
      version: versionData.number,
      endOfSupport: {
        isEndOfSupport: isEndOfSupportUI,
        endOfSupportDate:
          isNotNull(endOfSupportDateUI) && endOfSupportDateUI > 0
            ? dayjs(endOfSupportDateUI).toISOString()
            : null,
      },
    };
    sendUpdate(request);
  };
  return (
    <HeaderItem
      title={
        <Flex justify={'space-between'} align={'center'}>
          <div>End of Support</div>
          <div>
            {permissions.internalSystemAdministrator.hasPermissions(
              userProfile,
            ) &&
              !showEdit && (
                <IBTooltip
                  content={permissions.internalSystemAdministrator.tooltip}
                >
                  <IBDIcon
                    name="Edit"
                    title="Edit End of Support"
                    onClick={() => setShowEdit(true)}
                  />
                </IBTooltip>
              )}
          </div>
        </Flex>
      }
    >
      Has Reached End of Support: {isEndOfSupport ? 'Yes' : 'No'}
      {endOfSupportDisplayDate !== '' && (
        <div>End of Support Date: {endOfSupportDisplayDate}</div>
      )}
      {showEdit && (
        <Modal
          open={showEdit}
          onCancel={() => setShowEdit(false)}
          title={<ModalTitle>Edit End of Support</ModalTitle>}
          confirmLoading={false}
          footer={
            <ModalFooter>
              <Button onClick={() => setShowEdit(false)}>Close</Button>
              <Button onClick={handleSubmit} type="primary">
                Submit
              </Button>
            </ModalFooter>
          }
        >
          <Flex justify={'space-between'} align={'center'}>
            <div>
              <FormFieldLabelLeft>
                Has Reached End of Support:
              </FormFieldLabelLeft>
            </div>
            <div>
              {!isEndOfSupportUI ? <b>No</b> : <span>No</span>}{' '}
              <Switch
                value={isEndOfSupportUI}
                onChange={(checked: boolean) => setIsEndOfSupportUI(checked)}
              />{' '}
              {isEndOfSupportUI ? <b>Yes</b> : <span>Yes</span>}
            </div>
          </Flex>
          <Flex justify={'space-between'} align={'center'}>
            <div>
              <FormFieldLabelLeft>End of Support Date:</FormFieldLabelLeft>
            </div>
            <div>
              <DatePicker
                value={
                  endOfSupportDateUI ? ParseDate(endOfSupportDateUI) : null
                }
                onChange={(date) => setEndOfSupportDateUI(date.valueOf())}
              />
              <IBDIcon
                name={'Delete'}
                onClick={() => setEndOfSupportDateUI(null)}
              />
            </div>
          </Flex>
        </Modal>
      )}
    </HeaderItem>
  );
};
export default VersionEndOfSupport;
